// @ts-strict-ignore
import { useMemo } from 'react'
import { isArray, isEmpty } from 'lodash-es'
import { COMPANY_REQUIRED_DOCUMENTS } from '../helpers/constants'
import {
  CustomerDocument,
  DocumentRequirement,
  DocumentType,
  MergedDocument,
  RequiredButNotUploadedDocument,
  RequiredDocument,
  UploadedCustomerDocument,
} from '../helpers/types'

const getIndividualRequiredDocuments = (
  infoRequiredDocuments: DocumentRequirement[]
): RequiredDocument[] => {
  if (isEmpty(infoRequiredDocuments)) return []
  return infoRequiredDocuments.map(doc => ({
    type: doc.types,
    subType: doc.docSetType,
    nameKey: doc.docSetType.toLowerCase(),
  }))
}

const getRequiredType = (type?: DocumentType): string => {
  if (!type) return ''
  if (isArray(type)) return type?.[0]
  return type
}

const initialRequiredDocuments = (
  reqDoc: RequiredDocument,
  isCompany: boolean
): CustomerDocument => {
  const { type, subType = '' } = reqDoc || {}
  const requiredType = getRequiredType(type)
  const resolvedSubType = isCompany ? subType : subType || requiredType

  return {
    reference: '',
    externalReference: '',
    type: requiredType,
    name: requiredType,
    description: '',
    status: 'PENDING',
    subType: resolvedSubType,
  }
}

const listRequiredDocuments = (isCompany: boolean, infoRequiredDocuments: DocumentRequirement[]) =>
  isCompany ? COMPANY_REQUIRED_DOCUMENTS : getIndividualRequiredDocuments(infoRequiredDocuments)

const isOtherStandalone = (reqDoc: RequiredDocument) => {
  const { type } = reqDoc || {}
  return type === 'OTHER'
}

const buildNotUploadedRequired = (
  isCompany: boolean,
  infoRequiredDocuments: DocumentRequirement[]
): RequiredButNotUploadedDocument[] =>
  listRequiredDocuments(isCompany, infoRequiredDocuments)
    .filter(reqDoc => !isOtherStandalone(reqDoc))
    .map(reqDoc => ({ ...initialRequiredDocuments(reqDoc, isCompany), isUploaded: false }))

const countBaseRequired = (
  isCompany: boolean,
  infoRequiredDocuments: DocumentRequirement[]
): number =>
  listRequiredDocuments(isCompany, infoRequiredDocuments)?.filter(({ type }) => type !== 'OTHER')
    ?.length

const hasOtherUploadedDocuments = (receivedDocuments: CustomerDocument[]) =>
  receivedDocuments?.some(doc => doc?.type === 'OTHER')

const findMatchingReceivedDoc = (
  reqDoc: RequiredDocument,
  isCompany: boolean,
  receivedDocsMap: Map<string, CustomerDocument[]>,
  consumedDocReferences: Set<string>
): CustomerDocument | undefined => {
  const { type, subType = '' } = reqDoc || {}
  const typesArray = isArray(type) ? type : [type]
  const keys = (isCompany ? [subType] : [...typesArray, subType])?.filter(Boolean)

  for (const key of keys) {
    const match = receivedDocsMap?.get(key)?.find(doc => !consumedDocReferences.has(doc.reference))

    if (!match) continue

    consumedDocReferences.add(match.reference)
    return match
  }

  return undefined
}

const getReceivedDocuments = (receivedDocuments: CustomerDocument[], isCompany: boolean) => {
  const receivedDocsMap = new Map<string, CustomerDocument[]>()

  const addToMap = (key: string, doc: CustomerDocument) => {
    const existingDocs = receivedDocsMap.get(key)

    if (!existingDocs) {
      receivedDocsMap.set(key, [doc])
      return
    }

    existingDocs.push(doc)
  }

  for (const doc of receivedDocuments) {
    if (isCompany && doc?.subType) addToMap(doc.subType, doc)
    if (isCompany) continue

    // Individual docs may be keyed by either type or subType depending on API payload shape.
    const documentKeys = [doc?.type, doc?.subType]?.filter(Boolean)
    documentKeys?.forEach(key => addToMap(key, doc))
  }

  return { receivedDocsMap }
}

const getOtherDocuments = (
  receivedDocuments: CustomerDocument[],
  receivedDocsMap: Map<string, CustomerDocument[]>,
  isCompany: boolean,
  infoRequiredDocuments: DocumentRequirement[]
) => {
  const uploadedDocs: UploadedCustomerDocument[] = receivedDocuments.map(
    (doc): UploadedCustomerDocument => ({ ...doc, isUploaded: true })
  )
  const requiredDocs: RequiredButNotUploadedDocument[] = []
  const requiredDocuments = listRequiredDocuments(isCompany, infoRequiredDocuments)
  const consumedDocReferences = new Set<string>()

  for (const reqDoc of requiredDocuments) {
    const match = isCompany
      ? findMatchingReceivedDoc(reqDoc, isCompany, receivedDocsMap, consumedDocReferences)
      : undefined

    if ((isCompany && match) || isOtherStandalone(reqDoc)) continue
    requiredDocs?.push({ ...initialRequiredDocuments(reqDoc, isCompany), isUploaded: false })
  }

  return { uploadedDocs, requiredDocs }
}

export const useMergedDocuments = (
  receivedDocuments: CustomerDocument[],
  isCompany: boolean,
  infoRequiredDocuments: DocumentRequirement[]
) => {
  return useMemo(() => {
    const hasInfoRequiredDocuments = !!infoRequiredDocuments?.length

    if (!receivedDocuments || isEmpty(receivedDocuments)) {
      const mergedDocuments: RequiredButNotUploadedDocument[] = buildNotUploadedRequired(
        isCompany,
        infoRequiredDocuments
      )

      const individualRequiredDocuments = getIndividualRequiredDocuments(infoRequiredDocuments)

      const requiredCount = listRequiredDocuments(isCompany, infoRequiredDocuments)?.filter(
        reqDoc => reqDoc?.type !== 'OTHER'
      ).length
      return {
        mergedDocuments,
        uploadedCount: 0,
        requiredCount,
        getIndividualRequiredDocuments,
        individualRequiredDocuments,
      }
    }

    if (!isCompany && !hasInfoRequiredDocuments) {
      return {
        mergedDocuments: receivedDocuments.map(doc => ({ ...doc, isUploaded: true })),
        uploadedCount: receivedDocuments.length,
        requiredCount: 0,
        getIndividualRequiredDocuments,
      }
    }

    const { receivedDocsMap } = getReceivedDocuments(receivedDocuments, isCompany)
    const { uploadedDocs, requiredDocs } = getOtherDocuments(
      receivedDocuments,
      receivedDocsMap,
      isCompany,
      infoRequiredDocuments
    )

    const mergedDocuments: MergedDocument[] = [...uploadedDocs, ...requiredDocs]

    const uploadedCount = uploadedDocs?.length
    const baseRequiredTotal = countBaseRequired(isCompany, infoRequiredDocuments)

    const requiredCount = baseRequiredTotal + (hasOtherUploadedDocuments(receivedDocuments) ? 1 : 0)

    return { mergedDocuments, uploadedCount, requiredCount, getIndividualRequiredDocuments }
  }, [receivedDocuments, isCompany, infoRequiredDocuments])
}
